import {
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { type CashPair, MoneyPair, OverShortPair } from '../../components/CashFigures';
import { useLocationId } from '../../hooks/useSeedLocation';
import { formatDateTime, formatMoney } from '../../lib/format';
import { intlLocale, type PortalLocale, portalLocale } from '../../lib/locale';
import { notifyDone, notifyProblem } from '../../lib/notify';
import { CountSheet, useCountSheet } from './CountSheet';
import { registerPageCopy } from './registerPageCopy';

/**
 * WHAT THE OWNER STILL HAS TO LOOK AT, AND THE TWO THINGS HE OPENS TO LOOK.
 *
 * Everything on this panel exists because the controls it replaced named a person who is not in the
 * building. One bartender works a shift, and staff have no accounts on this site at all — so
 * "somebody else has to verify the closing count", "somebody else has to watch this go into the
 * safe" and "somebody other than the person who took it to the bank has to confirm it" each refused
 * every gesture they were ever asked about, under screens saying the control was working.
 *
 * His own words for what he wanted instead:
 *
 *   > "This would be done more by me from the Admin Portal as an audit … when I review a nightly
 *   > drop I should be able to see how much is supposed to be in that drop and either the money is
 *   > all there or it isn't."
 *
 * So: a list of what he has not looked at, and, for each kind of thing on it, the shortest possible
 * way of looking. **Nothing on this list blocks anything.** A drawer closes, a drop goes into the
 * safe and a night ends whether or not he has been near this panel; what he loses by ignoring it is
 * knowing, not trading. Which kinds appear here is his own setting.
 *
 * ## Signing a count off, and counting the box yourself, are different acts
 *
 * A closing count is somebody else's arithmetic and he is agreeing with it — one press, no money
 * moves, and if he does not believe it he counts the drawer himself instead of signing.
 *
 * The safe and the change box are the opposite: nobody has counted them at all. His count is the
 * only count there has ever been, and it is what the whole derived balance hangs off — everything
 * that has moved since is added to it. That is why a safe reads "never counted" until he does this
 * once, and why counting it is what lets money be banked out of it again.
 */

interface AwaitingCount {
  cashCountId: string;
  drawerSessionId: string | null;
  registerName: string | null;
  boxName: string | null;
  type: 'opening' | 'closing' | 'adhoc' | 'safe' | 'cajaita';
  countedAt: string;
  counterName: string | null;
}

interface AwaitingDrop {
  cashTransferId: string;
  at: string;
  intoName: string | null;
  amount: CashPair;
}

interface Awaiting {
  counts: AwaitingCount[];
  drops: AwaitingDrop[];
}

interface CheckResult {
  expected: CashPair | null;
  counted: CashPair;
  variance: CashPair | null;
  allThere: boolean;
  checkedByName: string | null;
  /**
   * The standing-float reconciliation, on a box that has one. Optional because the pouch check and
   * the sign-off answer with the same shape and carry none of this; null on a drawer — which
   * answers to its shift — and on a box with no standing float, which is the safe.
   */
  standingFloat?: CashPair | null;
  /** Bills paid straight out of the box since the count this figure is anchored on. */
  receiptsSinceAnchor?: CashPair | null;
  /** Standing float less counted-cash-plus-receipts, per currency. Positive is short. */
  shortOfStanding?: CashPair | null;
}

export const AWAITING_KEY = listKey('/admin/v1/cash/awaiting-check');

export function TheOwnersChecks({ locale = portalLocale() }: { locale?: PortalLocale }) {
  const queryClient = useQueryClient();
  const locationId = useLocationId();
  const text = registerPageCopy(locale).waiting;
  const dateLocale = intlLocale(locale);
  const where = locationId ? `?locationId=${encodeURIComponent(locationId)}` : '';
  const [openingDrop, setOpeningDrop] = useState<AwaitingDrop | null>(null);

  const waiting = useQuery({
    queryKey: AWAITING_KEY,
    queryFn: () => rawApi<Awaiting>('GET', `/admin/v1/cash/awaiting-check${where}`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: AWAITING_KEY });
    void queryClient.invalidateQueries({ queryKey: listKey('/admin/v1/cash/balances') });
  };

  const signOff = useMutation({
    mutationFn: (cashCountId: string) =>
      rawApi<CheckResult>('POST', `/admin/v1/cash/counts/${cashCountId}/check`, {}),
    onSuccess: (answer) => {
      const notice = text.audit.signOff.notice(answer.allThere);
      notifyDone(notice.title, notice.message);
      refresh();
    },
    onError: () => notifyProblem(text.audit.signOff.problem.title, text.audit.signOff.problem.message),
  });

  const counts = waiting.data?.counts ?? [];
  const drops = waiting.data?.drops ?? [];
  const nothing = counts.length === 0 && drops.length === 0;

  return (
    <Stack gap="sm">
      {nothing && !waiting.isLoading && (
        <Text size="sm" c="dimmed">
          {text.nothing}
        </Text>
      )}

      {counts.map((row) => (
        <Card key={row.cashCountId} withBorder>
          <Group justify="space-between" wrap="wrap" gap="xs">
            <div>
              <Group gap={6}>
                <Text size="sm" fw={600}>
                  {text.countType[row.type]}
                </Text>
                <Badge size="xs" variant="light">
                  {row.registerName ?? row.boxName ?? text.cash}
                </Badge>
              </Group>
              <Text size="xs" c="dimmed">
                {row.counterName ? `${row.counterName} · ` : ''}
                {formatDateTime(row.countedAt, dateLocale)}
              </Text>
            </div>
            <Group gap="xs">
              {/* Reading the figures is the point of signing, so the drawer is one click away. If he
                  does not believe them he counts it himself rather than putting his name to it. */}
              {row.drawerSessionId && (
                /* A router link, not a bare href: an anchor here reloaded the whole application to
                   move between two screens of it, throwing away every cached read and re-running
                   the sign-in bootstrap on the way. */
                <Button
                  size="compact-xs"
                  variant="subtle"
                  component={Link}
                  to={`/register/drawers/${row.drawerSessionId}`}
                >
                  {text.openDrawer}
                </Button>
              )}
              <Button
                size="compact-xs"
                variant="light"
                loading={signOff.isPending && signOff.variables === row.cashCountId}
                onClick={() => signOff.mutate(row.cashCountId)}
              >
                {text.checked}
              </Button>
            </Group>
          </Group>
        </Card>
      ))}

      {drops.map((row) => (
        <Card key={row.cashTransferId} withBorder>
          <Group justify="space-between" wrap="wrap" gap="xs">
            <div>
              <Text size="sm" fw={600}>
                {text.pouchInto(row.intoName)}
              </Text>
              <Text size="xs" c="dimmed">
                {formatDateTime(row.at, dateLocale)} · {text.countedIntoBag}
              </Text>
            </div>
            <Group gap="sm">
              <MoneyPair pair={row.amount} fw={700} />
              <Button size="compact-xs" variant="light" onClick={() => setOpeningDrop(row)}>
                {text.openAndCount}
              </Button>
            </Group>
          </Group>
        </Card>
      ))}

      <OpenThePouch
        drop={openingDrop}
        locale={locale}
        onClose={() => setOpeningDrop(null)}
        onChecked={refresh}
      />
    </Stack>
  );
}

/**
 * The pouch, opened.
 *
 * The figure that was counted into it is on the card behind this modal and deliberately not inside
 * it: he is counting what is in his hands, and a target on the screen while he does that is a target
 * the counting drifts towards. The comparison arrives when he submits.
 */
function OpenThePouch({
  drop,
  locale,
  onClose,
  onChecked,
}: {
  drop: AwaitingDrop | null;
  locale: PortalLocale;
  onClose: () => void;
  onChecked: () => void;
}) {
  const locationId = useLocationId();
  const text = registerPageCopy(locale).waiting.audit;
  const where = locationId ? `?locationId=${encodeURIComponent(locationId)}` : '';
  const sheet = useCountSheet();
  const [note, setNote] = useState('');
  const [result, setResult] = useState<CheckResult | null>(null);

  const check = useMutation({
    mutationFn: () =>
      rawApi<CheckResult>('POST', `/admin/v1/cash/transfers/${drop?.cashTransferId}/audit${where}`, {
        denominationLines: sheet.lines(),
        note: note || null,
      }),
    onSuccess: (answer) => {
      setResult(answer);
      const notice = text.pouch.notice(answer.allThere);
      notifyDone(notice.title, notice.message);
      onChecked();
    },
    onError: () => notifyProblem(text.pouch.problem.title, text.pouch.problem.message),
  });

  const close = () => {
    sheet.clear();
    setNote('');
    setResult(null);
    onClose();
  };

  return (
    <Modal opened={drop !== null} onClose={close} title={text.pouch.title} size="lg">
      {result ? (
        <Stack gap="sm">
          <Group gap="lg">
            <div>
              <Text size="xs" c="dimmed">
                {text.pouch.wentIntoBag}
              </Text>
              <MoneyPair pair={result.expected} fw={700} />
            </div>
            <div>
              <Text size="xs" c="dimmed">
                {text.common.youCounted}
              </Text>
              <MoneyPair pair={result.counted} fw={700} />
            </div>
            <div>
              <Text size="xs" c="dimmed">
                {text.common.difference}
              </Text>
              <OverShortPair pair={result.variance} />
            </div>
          </Group>
          <Alert color={result.allThere ? 'green' : 'orange'} variant="light">
            {result.allThere ? text.pouch.exactResult : text.pouch.differentResult}
          </Alert>
          <Group justify="flex-end">
            <Button onClick={close}>{text.common.done}</Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="sm">
          <CountSheet
            locale={locale}
            notes={sheet.notes}
            loading={sheet.loading}
            /* A count cannot post empty by accident — nothing can be typed into a sheet with no
               rows, and the submit below stays off until something is — so a failed read costs
               honesty rather than money: without this, the modal claims no notes are configured. */
            error={sheet.error}
            sheet={sheet.sheet}
            onChange={(key, value) => sheet.setSheet({ ...sheet.sheet, [key]: value })}
          />
          <Textarea
            label={text.common.note}
            description={text.pouch.noteDescription}
            autosize
            minRows={1}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
          <Group justify="space-between">
            <Group gap={6}>
              <Text size="sm" c="dimmed">
                {text.common.onSheet}
              </Text>
              <MoneyPair pair={sheet.running} fw={700} />
            </Group>
            <Button
              disabled={!sheet.anythingTyped || check.isPending}
              loading={check.isPending}
              onClick={() => check.mutate()}
            >
              {text.pouch.record}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

/**
 * COUNTING THE SAFE OR THE CHANGE BOX, which nothing in this system could do until now.
 *
 * `cash_counts` has carried a `safe` type since the schema was first drawn, and the safe's balance
 * has always been derived from the last count somebody CHECKED — but checking meant a second member
 * of staff, so there could never be one. The safe therefore read as everything that had ever moved
 * through it, which on any real site is wrong from the first morning: the cash in it before this
 * software existed was never recorded arriving, and a deposit out of it was refused for ever.
 *
 * This count re-anchors the figure. Nothing before it counts and everything after it does.
 */
export function CountTheBox({
  box,
  locale = portalLocale(),
  onClose,
  onCounted,
}: {
  box: { id: string; name: string } | null;
  locale?: PortalLocale;
  onClose: () => void;
  onCounted: () => void;
}) {
  const locationId = useLocationId();
  const text = registerPageCopy(locale).waiting.audit;
  const where = locationId ? `?locationId=${encodeURIComponent(locationId)}` : '';
  const sheet = useCountSheet();
  const [note, setNote] = useState('');
  const [result, setResult] = useState<CheckResult | null>(null);

  const count = useMutation({
    mutationFn: () =>
      rawApi<CheckResult>('POST', `/admin/v1/cash/locations/${box?.id}/audit${where}`, {
        denominationLines: sheet.lines(),
        note: note || null,
      }),
    onSuccess: (answer) => {
      setResult(answer);
      notifyDone(text.box.notice.title, text.box.notice.message);
      onCounted();
    },
    onError: () => notifyProblem(text.box.problem.title, text.box.problem.message),
  });

  const close = () => {
    sheet.clear();
    setNote('');
    setResult(null);
    onClose();
  };

  return (
    <Modal opened={box !== null} onClose={close} title={text.box.title(box?.name ?? null)} size="lg">
      {result ? (
        <Stack gap="sm">
          <Group gap="lg">
            <div>
              <Text size="xs" c="dimmed">
                {text.box.ledgerExpected}
              </Text>
              <MoneyPair pair={result.expected} fw={700} />
            </div>
            <div>
              <Text size="xs" c="dimmed">
                {text.common.youCounted}
              </Text>
              <MoneyPair pair={result.counted} fw={700} />
            </div>
            <div>
              <Text size="xs" c="dimmed">
                {text.common.difference}
              </Text>
              <OverShortPair pair={result.variance} />
            </div>
          </Group>
          <Alert color="gray" variant="light">
            {text.box.resultDescription}
          </Alert>
          {result.standingFloat && <AgainstTheStandingFloat result={result} locale={locale} />}
          <Group justify="flex-end">
            <Button onClick={close}>{text.common.done}</Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="sm">
          <Title order={6}>{text.box.contentsTitle}</Title>
          <CountSheet
            locale={locale}
            notes={sheet.notes}
            loading={sheet.loading}
            /* Same shape as the pouch above: no wipe is possible from here, but a failed read must
               not read as "this bar has no notes" while the safe sits uncountable. */
            error={sheet.error}
            sheet={sheet.sheet}
            onChange={(key, value) => sheet.setSheet({ ...sheet.sheet, [key]: value })}
          />
          <Textarea
            label={text.common.note}
            description={text.box.noteDescription}
            autosize
            minRows={1}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
          <Group justify="space-between">
            <Group gap={6}>
              <Text size="sm" c="dimmed">
                {text.common.onSheet}
              </Text>
              <MoneyPair pair={sheet.running} fw={700} />
            </Group>
            <Button
              disabled={!sheet.anythingTyped || count.isPending}
              loading={count.isPending}
              onClick={() => count.mutate()}
            >
              {text.box.record}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

/**
 * THE OWNER'S OWN SENTENCE, made real under his count.
 *
 * His rule for the change box, verbatim: the totals should always remain the same, "although it is
 * possible that some invoices for instance water or ice deliveries may be paid out of the Cajita,
 * so receipts plus cash should still total those amounts." That is a different question from the
 * variance above — the variance measures the count against the ledger and catches missing money;
 * this measures cash-plus-receipts against what the box is kept stocked to, and a box can be whole
 * here while carrying a large and honest variance there (the first count of a box the ledger has
 * never seen inside is exactly that).
 *
 * The verdict is the server's `shortOfStanding`, never re-derived here. The one sum this block
 * does — counted plus receipts — is display, showing the two halves of his sentence side by side
 * before the figure they are measured against.
 */
function AgainstTheStandingFloat({ result, locale }: { result: CheckResult; locale: PortalLocale }) {
  const text = registerPageCopy(locale).waiting.audit.standing;
  const standing = result.standingFloat;
  if (!standing) return null;
  const receipts = result.receiptsSinceAnchor ?? { usdMinor: 0, hnlMinor: 0 };
  const together = {
    usdMinor: result.counted.usdMinor + receipts.usdMinor,
    hnlMinor: result.counted.hnlMinor + receipts.hnlMinor,
  };
  const shortOf = result.shortOfStanding ?? { usdMinor: 0, hnlMinor: 0 };
  // Positive is short — notes gone with no receipt to cover them. Negative is over-stocked.
  const phrase = (minor: number, currency: string) =>
    `${formatMoney(Math.abs(minor), currency)} ${minor > 0 ? text.short : text.over}`;
  const halves = [
    shortOf.usdMinor !== 0 ? phrase(shortOf.usdMinor, 'USD') : null,
    shortOf.hnlMinor !== 0 ? phrase(shortOf.hnlMinor, 'HNL') : null,
  ].filter((half): half is string => half !== null);
  const whole = halves.length === 0;
  const anyShort = shortOf.usdMinor > 0 || shortOf.hnlMinor > 0;
  const anyOver = shortOf.usdMinor < 0 || shortOf.hnlMinor < 0;
  const differences = halves.join(text.joiner);

  return (
    <>
      <Divider label={text.title} labelPosition="left" />
      <Text size="xs" c="dimmed">
        {text.description}
      </Text>
      <Group gap="lg">
        <div>
          <Text size="xs" c="dimmed">
            {text.cashCounted}
          </Text>
          <MoneyPair pair={result.counted} />
        </div>
        <div>
          <Text size="xs" c="dimmed">
            {text.receipts}
          </Text>
          <MoneyPair pair={receipts} />
        </div>
        <div>
          <Text size="xs" c="dimmed">
            {text.together}
          </Text>
          <MoneyPair pair={together} fw={700} />
        </div>
        <div>
          <Text size="xs" c="dimmed">
            {text.shouldHold}
          </Text>
          <MoneyPair pair={standing} fw={700} />
        </div>
      </Group>
      <Alert color={whole ? 'green' : anyShort ? 'orange' : 'gray'} variant="light">
        {whole
          ? text.exact
          : anyShort && anyOver
            ? text.mixed(differences)
            : anyShort
              ? text.shortage(differences)
              : text.overage(differences)}
      </Alert>
    </>
  );
}
